import { ariaAttr, callAllHandlers } from "@design-system/utils"
import { FocusEventHandler } from "react"
import { FormControlOptions, useFormControlContext } from "./form-control"

export interface UseFormControlProps<T extends HTMLElement>
  extends FormControlOptions {
  id?: string
  onFocus?: FocusEventHandler<T>
  onBlur?: FocusEventHandler<T>
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
}

/**
 * React hook that provides the props that should be spread on to
 * input fields (`input`, `select`, `textarea`, etc.).
 *
 * It provides a convenient way to control a form fields, validation
 * and helper text.
 */
export function useFormControl<T extends HTMLElement>(
  props: UseFormControlProps<T>,
) {
  const {
    isDisabled,
    isInvalid,
    isReadOnly,
    isRequired,
    ...rest
  } = useFormControlProps(props)

  return {
    ...rest,
    disabled: isDisabled,
    readOnly: isReadOnly,
    required: isRequired,
    "aria-invalid": ariaAttr(isInvalid),
    "aria-required": ariaAttr(isRequired),
    "aria-readonly": ariaAttr(isReadOnly),
  }
}

export function useFormControlProps<T extends HTMLElement>(
  props: UseFormControlProps<T>,
) {

  const {
    id,
    disabled,
    readOnly,
    required,
    isRequired,
    isInvalid,
    isReadOnly,
    isDisabled,
    onFocus,
    onBlur,
    ...rest
  } = props

  const labelIds: string[] = []

  return {
    ...rest,
    "aria-describedby": labelIds.join(" ") || undefined,
    id,
    isDisabled: disabled ?? isDisabled,
    isReadOnly: readOnly ?? isReadOnly,
    isRequired: required ?? isRequired,
    isInvalid,
    onFocus: callAllHandlers(onFocus),
    onBlur: callAllHandlers(onBlur),
  }
}
